import React from "react";
import { useClientSideSplitTreatments } from "@leafly-com/split-next";
import isEmpty from "lodash/isEmpty";
import Link from "next/link";

import { Action, Category } from "@/constants/events";
import useDomainCountryCode from "@/hooks/useDomainCountryCode";
import { useEventTracker } from "@/hooks/useEventTracker";
import { useSelector } from "@/redux/hooks";
import { getGeoSlug, getLocationState } from "@/redux/selectors/location";
import slugify from "@/utils/slugifyURLComponent";
import { getSplitKey } from "@/utils/split/getSplitKey";

export const HeaderLinks = () => {
  const userLocation = useSelector(getLocationState);

  const countryCode = useDomainCountryCode();

  const { publishEvent } = useEventTracker();

  const split = useClientSideSplitTreatments(
    ["NL-2110_dynamicNavLinkLocation_flag"],
    getSplitKey(),
  );

  const useLocationLink =
    !split.isLoading &&
    split.treatments["NL-2110_dynamicNavLinkLocation_flag"].treatment === "on";

  const locationUrl = !isEmpty(userLocation)
    ? `/dispensaries/${slugify(userLocation.state)}/${slugify(userLocation.city)}`
    : "/dispensaries/arizona/phoenix";

  const trackLinkClick = (eventLabel: string) => {
    publishEvent({
      action: Action.click,
      category: Category.header,
      label: eventLabel,
    });
  };

  const linkClasses = `mr-md mt-md font-bold whitespace-nowrap hover:underline`;
  const geoSlug = useSelector(getGeoSlug);
  return (
    <nav
      className="hidden lg:flex overflow-x-auto whitespace-nowrap"
      id="global-header-nav-links"
      aria-label="Popular pages"
    >
      <Link
        className={linkClasses}
        data-testid="shop"
        href={`/shop${geoSlug ? `?location=${geoSlug}` : ""}`}
        onClick={() => {
          trackLinkClick("shop");
        }}
      >
        Shop
      </Link>

      <Link
        className={linkClasses}
        data-testid="delivery"
        href="/delivery"
        onClick={() => {
          trackLinkClick("delivery");
        }}
      >
        Delivery
      </Link>

      <Link
        className={linkClasses}
        data-testid="dispensaries"
        href={useLocationLink ? locationUrl : "/dispensaries"}
        onClick={() => {
          trackLinkClick("dispensaries");
        }}
      >
        {countryCode === "CA" ? "Stores" : "Dispensaries"}
      </Link>

      <Link
        className={linkClasses}
        data-testid="deals"
        href="/deals"
        onClick={() => {
          trackLinkClick("deals");
        }}
      >
        Deals
      </Link>

      <Link
        className={linkClasses}
        data-testid="strains"
        href="/strains/lists"
        onClick={() => {
          trackLinkClick("strains");
        }}
      >
        Strains
      </Link>

      <Link
        className={linkClasses}
        data-testid="brands"
        href="/brands"
        onClick={() => {
          trackLinkClick("brands");
        }}
      >
        Brands
      </Link>

      <Link
        className={linkClasses}
        data-testid="products"
        href="/products"
        onClick={() => {
          trackLinkClick("products");
        }}
      >
        Products
      </Link>

      {countryCode !== "CA" && (
        <Link
          className={linkClasses}
          data-testid="cbd"
          href="/cbd-stores"
          onClick={() => {
            trackLinkClick("cbd");
          }}
        >
          CBD
        </Link>
      )}

      <Link
        className={linkClasses}
        data-testid="doctors"
        href={"/medical-marijuana-doctors"}
        onClick={() => {
          trackLinkClick("doctors");
        }}
      >
        Doctors
      </Link>

      <Link
        className={linkClasses}
        data-testid="cannabis-101"
        href="/news/cannabis-101"
        onClick={() => {
          trackLinkClick("cannabis 101");
        }}
      >
        Cannabis 101
      </Link>

      <Link
        className={linkClasses}
        data-testid="social-impact"
        href="/social-impact"
        onClick={() => {
          trackLinkClick("social impact");
        }}
      >
        Social impact
      </Link>
    </nav>
  );
};
